import React, { useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { HighlightContext } from 'features/agent-analytics/components/payloadEntityLinks';
import {
  RichValueView, JsonRawView, JsonTree, CopyButton, StatusCodePill, LevelBadge,
} from 'features/agent-analytics/components/runCells';
import { AnnotationList } from 'features/agent-analytics/annotations';

function isNonEmptyObject(o) {
  return o && typeof o === 'object' && !Array.isArray(o) && Object.keys(o).length > 0;
}

/** One block of the payload: a header you can click to fold it away, and
 *  the payload under it with no box around it. */
function Section({ label, children, copyValue, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <section className="py-3.5 first:pt-0 last:pb-0" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="group/row flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="-ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
        >
          <ChevronRight size={13} strokeWidth={2.25} className={`transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />
          {label}
        </button>
        {copyValue != null && copyValue !== '' ? <CopyButton value={copyValue} label={`Copy ${label}`} /> : null}
      </div>
      {open ? <div className="mt-2 min-w-0 pl-[18px] text-[13px] leading-relaxed">{children}</div> : null}
    </section>
  );
}

/** Pick the right view for one value based on the tab-level mode. */
function ValueView({ value, mode, onRunClick, onSpanClick }) {
  const entityProps = { onRunClick, onSpanClick };
  return mode === 'json'
    ? <JsonRawView value={value} {...entityProps} />
    : <RichValueView value={value} {...entityProps} />;
}
function ObjectView({ value, mode, onRunClick, onSpanClick }) {
  const entityProps = { onRunClick, onSpanClick };
  // Metadata / attributes render as a collapsible Path/Value tree in formatted mode.
  return mode === 'json'
    ? <JsonRawView value={value} {...entityProps} />
    : <JsonTree value={value} {...entityProps} />;
}

/**
 * What went wrong, leading Activity. It used to sit under the drawer's meta
 * grid, away from the input and output it explains; here it is the first
 * thing you read on a failed run or span.
 */
function ErrorSection({ type, message, statusCode, level, fallback }) {
  return (
    <Section label="Error" copyValue={message || undefined}>
      <div className="rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] px-3 py-2.5 text-[12px] text-[color:var(--feedback-error)]">
        {type || statusCode ? (
          <div className="mb-1 flex flex-wrap items-center gap-2">
            {type ? <span className="font-mono text-[11px] font-semibold">{type}</span> : null}
            <StatusCodePill code={statusCode} />
            <LevelBadge level={level} status="error" />
          </div>
        ) : null}
        {message
          ? <p className="whitespace-pre-wrap break-words">{message}</p>
          : <p className="opacity-70">{fallback}</p>}
      </div>
    </Section>
  );
}

function RunLevel({ run, mode, onRunClick, onSpanClick }) {
  const entityProps = { onRunClick, onSpanClick };
  const failed = run.status === 'error' || run.error_summary || run.error_type;
  return (
    <div className="flex min-w-0 flex-col divide-y divide-[color:var(--border-default)]">
      {failed ? (
        <ErrorSection type={run.error_type} message={run.error_summary} level={run.level} fallback="This run ended in an error." />
      ) : null}

      {run.input ? (
        <Section label="Input" copyValue={run.input}><ValueView value={run.input} mode={mode} {...entityProps} /></Section>
      ) : null}

      {run.output ? (
        <Section label="Output" copyValue={run.output}><ValueView value={run.output} mode={mode} {...entityProps} /></Section>
      ) : null}

      {isNonEmptyObject(run.metadata) ? (
        <Section label="Metadata" copyValue={run.metadata}><ObjectView value={run.metadata} mode={mode} {...entityProps} /></Section>
      ) : null}

      {isNonEmptyObject(run.attributes) ? (
        <Section label="Custom attributes" copyValue={run.attributes}><ObjectView value={run.attributes} mode={mode} {...entityProps} /></Section>
      ) : null}

      {!run.input && !run.output
        && !isNonEmptyObject(run.metadata)
        && !isNonEmptyObject(run.attributes)
        && !run.error_summary ? (
        <div className="rounded-lg border border-dashed border-[color:var(--border-default)] bg-surface p-4 text-center text-[12px] text-[color:var(--text-muted)]">
          No payload captured for this run.
        </div>
      ) : null}
    </div>
  );
}

/** Span payload — the error first when it failed, then input / output /
 *  attributes. The span header and meta tags live in the run-detail meta
 *  strip and evals in the Scores tab; the error is shown here only (the meta
 *  strip no longer repeats it). */
function SpanLevel({ span, mode, onRunClick, onSpanClick }) {
  const entityProps = { onRunClick, onSpanClick };
  const failed = span.status === 'error' || span.error_message || span.error_type;
  return (
    <div className="flex min-w-0 flex-col divide-y divide-[color:var(--border-default)]">
      {failed ? (
        <ErrorSection
          type={span.error_type}
          message={span.error_message}
          statusCode={span.status_code}
          level={span.level}
          fallback="Marked as errored (no message recorded)."
        />
      ) : null}

      {span.input ? (
        <Section label="Input" copyValue={span.input}><ValueView value={span.input} mode={mode} {...entityProps} /></Section>
      ) : null}

      {span.output ? (
        <Section label="Output" copyValue={span.output}><ValueView value={span.output} mode={mode} {...entityProps} /></Section>
      ) : null}

      {isNonEmptyObject(span.attributes) ? (
        <Section label="Attributes" copyValue={span.attributes}><ObjectView value={span.attributes} mode={mode} {...entityProps} /></Section>
      ) : null}

      {!span.input && !span.output && !isNonEmptyObject(span.attributes) && !span.error_message ? (
        <div className="rounded-lg border border-dashed border-[color:var(--border-default)] bg-surface p-4 text-center text-[12px] text-[color:var(--text-muted)]">
          No payload captured for this span.
        </div>
      ) : null}
    </div>
  );
}

/**
 * Activity tab body. A single tab-level Formatted | JSON toggle decides how
 * every Input / Output / metadata / attributes block in this panel renders:
 *
 *   - 'formatted' → text values render as chat-style markdown (with GFM
 *                   tables); JSON values render as recursive key/value tables.
 *   - 'json'      → every value renders as pretty-printed JSON in a <pre>.
 */
function copyValue(v) {
  if (v == null) return '';
  if (typeof v === 'object') { try { return JSON.stringify(v, null, 2); } catch { return String(v); } }
  return String(v);
}

/**
 * Build a full plain-text dump of everything shown in Activity — for the run
 * (all properties + input/output/metadata/attributes) or the selected span
 * (values, tokens, breakdown, input, output, errors, types, attributes).
 */
export function buildActivityCopyText(run, span) {
  const lines = [];
  const add = (label, val) => { if (val != null && val !== '') lines.push(`${label}: ${copyValue(val)}`); };
  if (span) {
    lines.push(`# Span: ${span.name || span.tool_name || span.span_id}`);
    add('span_id', span.span_id); add('parent_span_id', span.parent_span_id);
    add('kind', span.kind); add('name', span.name); add('status', span.status); add('level', span.level);
    add('model', span.model); add('provider', span.provider); add('tool_name', span.tool_name);
    add('temperature', span.temperature);
    add('input_tokens', span.input_tokens); add('output_tokens', span.output_tokens); add('cost', span.cost);
    add('started_at', span.started_at); add('ended_at', span.ended_at); add('duration_ms', span.duration_ms);
    add('error_type', span.error_type); add('error_message', span.error_message);
    add('status_code', span.status_code); add('stack_trace', span.stack_trace);
    add('input', span.input); add('output', span.output); add('attributes', span.attributes);
    add('usage_details', span.usage_details); add('cost_details', span.cost_details);
  } else if (run) {
    lines.push(`# Run: ${run.agent_name || run.run_id}`);
    add('run_id', run.run_id); add('agent_name', run.agent_name); add('parent_run_id', run.parent_run_id);
    add('status', run.status); add('level', run.level);
    add('distinct_id', run.distinct_id); add('conversation_id', run.conversation_id);
    add('total_tokens_in', run.total_tokens_in); add('total_tokens_out', run.total_tokens_out);
    add('total_cost', run.total_cost);
    add('started_at', run.started_at); add('ended_at', run.ended_at); add('duration_ms', run.duration_ms);
    add('span_count', run.span_count); add('tool_count', run.tool_count); add('error_count', run.error_count);
    add('error_type', run.error_type); add('error_summary', run.error_summary);
    add('rage_score', run.rage_score); add('satisfaction_score', run.satisfaction_score);
    add('feedback_satisfaction', run.feedback_satisfaction); add('feedback_rating', run.feedback_rating);
    add('feedback_comment', run.feedback_comment);
    add('input', run.input); add('output', run.output);
    add('metadata', run.metadata); add('attributes', run.attributes);
  }
  return lines.join('\n');
}

export default function RunActivityPanel({
  teamId, run, selectedSpan, onClearSpan, onRunClick, onSpanClick, highlight = null, mode = 'formatted',
}) {
  const entityProps = { onRunClick, onSpanClick };
  const bodyRef = useRef(null);

  // The highlight applies only when the view it targets is the one on screen:
  // a span-level hit needs that span selected; a run-level hit needs none.
  const appliesHere = highlight
    && (selectedSpan ? highlight.spanId === selectedSpan.span_id : highlight.spanId == null);

  // Terms mark every word that contributed; passage marks the sentence the
  // backend identified as the reason, and becomes the spotlight target.
  // `query` is still honoured so callers that only have a raw string (global
  // search arrivals) keep working.
  const activeHighlight = React.useMemo(() => {
    if (!appliesHere) return '';
    const terms = highlight.terms?.length ? highlight.terms : (highlight.query ? [highlight.query] : []);
    if (!terms.length && !highlight.passage) return '';
    return { terms, passage: highlight.passage || '', whole: Boolean(highlight.terms?.length) };
  }, [appliesHere, highlight?.terms, highlight?.passage, highlight?.query]);

  // Scroll the match into view and spotlight it.
  //
  // This used to grab the FIRST <mark> on the page, which is only the right
  // one by luck — a query word appearing in a system prompt above the actual
  // hit won every time. Prefer the passage mark, which is the located reason,
  // and fall back to the first term mark. The pulse is what makes the landing
  // legible: without it the user arrives at a scrolled position with no idea
  // what moved.
  useEffect(() => {
    if (!appliesHere || !activeHighlight) return undefined;
    let cleanup = null;
    const id = requestAnimationFrame(() => {
      const root = bodyRef.current;
      if (!root) return;
      const target = root.querySelector('mark[data-spotlight="true"]') || root.querySelector('mark');
      if (!target) return;
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target.classList.add('trodo-spotlight');
      const t = setTimeout(() => target.classList.remove('trodo-spotlight'), 2200);
      cleanup = () => { clearTimeout(t); target.classList.remove('trodo-spotlight'); };
    });
    return () => { cancelAnimationFrame(id); if (cleanup) cleanup(); };
  }, [appliesHere, activeHighlight, highlight?.ts, mode, selectedSpan?.span_id]);

  if (!run) return null;
  return (
    <div className="flex min-w-0 flex-col gap-3" ref={bodyRef}>
      <HighlightContext.Provider value={activeHighlight}>
        {selectedSpan
          ? <SpanLevel span={selectedSpan} mode={mode} {...entityProps} />
          : <RunLevel run={run} mode={mode} {...entityProps} />}
      </HighlightContext.Provider>
    </div>
  );
}

/**
 * Annotations on the run, or on the span selected in the tree — the
 * drawer's Annotations tab.
 *
 * They used to be the last section of Activity, below Metadata and Custom
 * attributes, where nobody found them. With Signals gone the tab strip has
 * room, and a tab with a count says comments exist before you scroll.
 */
export function RunCommentsPanel({ run, selectedSpan, onOpenContext }) {
  if (!run) return null;
  return (
    <AnnotationList
      layout="section"
      conversationId={selectedSpan ? selectedSpan.conversation_id : run.conversation_id}
      scope={selectedSpan ? 'span' : 'run'}
      runId={run.run_id}
      spanId={selectedSpan?.span_id || null}
      spanStartedAt={selectedSpan?.started_at}
      lockedContext={selectedSpan
        ? {
          type: 'span',
          id: selectedSpan.span_id,
          runId: run.run_id,
          startedAt: selectedSpan.started_at,
          label: selectedSpan.name || selectedSpan.tool_name || selectedSpan.span_id,
        }
        : {
          type: 'run',
          id: run.run_id,
          label: `Run ${run.run_id || ''}`,
        }}
      onOpenContext={onOpenContext}
    />
  );
}
